import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { AppButton, AppEmptyState, AppPillSwitch } from '../../components/ui';
import { formatIsoDate, formatMinutes, formatWeekLabel, mondayOf, shiftWeek } from '../../utils/tempsWeek';

const SEMAINE_SUBVIEWS = [
  { value: 'planning', label: 'Planning', icon: 'mdi:clipboard-list-outline' },
  { value: 'equipe', label: 'Équipe', icon: 'mdi:account-group-outline' },
];

const ASSIGN_FILTERS = [
  { value: 'all', label: 'Toutes' },
  { value: 'assigned', label: 'Assignées' },
  { value: 'unassigned', label: 'Non assignées' },
];

const SCHEDULE_OPTS = [
  { value: 'this_week', label: 'Cette semaine', icon: 'mdi:calendar-check' },
  { value: 'next_week', label: 'Semaine +1', icon: 'mdi:calendar-plus' },
  { value: 'remind_later', label: 'Plus tard', icon: 'mdi:alarm' },
];

const PERSON_COLORS = ['#2f6fb5', '#a4552c', '#2e7d6b', '#5f6a2a', '#b0463f', '#4d5fa0', '#8a6420', '#3f6f8f'];

const initials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
};

const colorForUser = (id) => {
  const n = Number(id) || 0;
  return PERSON_COLORS[Math.abs(n) % PERSON_COLORS.length];
};

const progressTone = (pct) => {
  if (pct >= 70) return '';
  if (pct >= 35) return 'is-warn';
  return 'is-danger';
};

const scheduleActive = (task, value, week) => {
  if (value === 'remind_later') return Boolean(task.remindLaterUntil);
  if (value === 'this_week') return task.weekStart === week && !task.remindLaterUntil;
  if (value === 'next_week') return task.weekStart === shiftWeek(week, 1) && !task.remindLaterUntil;
  return false;
};

const AssigneeControl = ({ task, users, onAssignee }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = task.assignee;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="tp-board__assign-menu" ref={ref} data-pmenu="1">
      <button
        type="button"
        className="tp-board__assign-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Assignée à"
        onClick={() => setOpen((v) => !v)}
      >
        {current ? (
          <span className="tp-board__avatar tp-board__avatar--sm" style={{ background: colorForUser(current.id) }}>
            {initials(current.name)}
          </span>
        ) : (
          <span className="tp-board__avatar tp-board__avatar--sm tp-board__avatar--ghost" />
        )}
        <span className="flex-grow-1 text-truncate">{current?.name || 'Non assignée'}</span>
        <Icon icon="mdi:unfold-more-horizontal" width={16} />
      </button>
      {open && (
        <div className="tp-board__assign-dropdown" role="listbox" aria-label="Choisir une personne">
          <button
            type="button"
            role="option"
            aria-selected={!current}
            className={`tp-board__assign-option${!current ? ' is-active' : ''}`}
            onClick={() => {
              onAssignee(task, null);
              setOpen(false);
            }}
          >
            <span className="tp-board__avatar tp-board__avatar--sm tp-board__avatar--ghost" />
            <span className="flex-grow-1">Non assignée</span>
            {!current && <Icon icon="mdi:check" width={16} style={{ color: 'var(--app-accent)' }} />}
          </button>
          {users.map((user) => {
            const active = Number(current?.id) === Number(user.id);
            return (
              <button
                key={user.id}
                type="button"
                role="option"
                aria-selected={active}
                className={`tp-board__assign-option${active ? ' is-active' : ''}`}
                onClick={() => {
                  onAssignee(task, user.id);
                  setOpen(false);
                }}
              >
                <span className="tp-board__avatar tp-board__avatar--sm" style={{ background: colorForUser(user.id) }}>
                  {initials(user.name)}
                </span>
                <span className="flex-grow-1 text-truncate">{user.name}</span>
                {active && <Icon icon="mdi:check" width={16} style={{ color: 'var(--app-accent)' }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const PlanControl = ({ task, week, onSchedule }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = SCHEDULE_OPTS.find((opt) => scheduleActive(task, opt.value, week)) || null;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="tp-board__plan-menu" ref={ref} data-pmenu="1">
      <button
        type="button"
        className="tp-board__assign-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Planning"
        onClick={() => setOpen((v) => !v)}
      >
        <Icon icon={current?.icon || 'mdi:calendar-blank-outline'} width={16} className="tp-board__plan-icon" />
        <span className="flex-grow-1 text-truncate">{current?.label || 'Planning'}</span>
        <Icon icon="mdi:unfold-more-horizontal" width={16} />
      </button>
      {open && (
        <div className="tp-board__assign-dropdown" role="listbox" aria-label="Choisir un planning">
          {SCHEDULE_OPTS.map((opt) => {
            const active = current?.value === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={active}
                className={`tp-board__assign-option${active ? ' is-active' : ''}`}
                onClick={() => {
                  onSchedule(task, opt.value);
                  setOpen(false);
                }}
              >
                <Icon icon={opt.icon} width={16} className="tp-board__plan-icon" />
                <span className="flex-grow-1 text-truncate">{opt.label}</span>
                {active && <Icon icon="mdi:check" width={16} style={{ color: 'var(--app-accent)' }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const TaskRow = ({
  task,
  week,
  users,
  onAssignee,
  onSchedule,
  onArchive,
  onDelete,
  remind = false,
}) => (
  <div className="tp-board__row" role="listitem">
    <div className="tp-board__row-main">
      <div className="tp-board__row-title">
        <span>{task.title}</span>
        {task.status === 'validee' && (
          <span className="small" style={{ color: 'var(--app-ok)', fontWeight: 600 }}>
            <Icon icon="mdi:check-circle" width={14} className="me-1" />
            Validée
          </span>
        )}
      </div>
      <div className="tp-board__row-meta" title={`${task.productLabel || ''} · commande #${task.commandeId}`}>
        {task.productLabel || task.productRef || 'Tâche'} · commande #{task.commandeId}
        {remind && task.remindLaterUntil ? ` · retour ${task.remindLaterUntil}` : ''}
      </div>
    </div>
    <div className="tp-board__row-controls">
      {!remind ? (
        <AssigneeControl task={task} users={users} onAssignee={onAssignee} />
      ) : (
        <div className="tp-board__remind-hint">
          <Icon icon="mdi:restore" width={14} />
          Reviendra non assignée le {task.remindLaterUntil || 'lundi suivant'}
        </div>
      )}
      <PlanControl task={task} week={week} onSchedule={onSchedule} />
      <div className="tp-board__time">
        <strong>{formatMinutes(task.estimatedMinutes)}</strong>
        <span className="sep"> / </span>
        <span className={task.actualMinutes ? 'done' : ''}>
          {task.actualMinutes != null ? formatMinutes(task.actualMinutes) : '—'}
        </span>
      </div>
      <div className="tp-board__actions">
        {!remind && task.status === 'validee' && (
          <AppButton size="sm" variant="outline-secondary" icon="mdi:archive-outline" onClick={() => onArchive(task)}>
            Archiver
          </AppButton>
        )}
        <button type="button" className="tp-board__icon-btn" title="Supprimer" aria-label="Supprimer" onClick={() => onDelete(task)}>
          <Icon icon="mdi:trash-can-outline" width={18} />
        </button>
      </div>
    </div>
  </div>
);

const TempsPlanningBoard = ({
  week,
  onWeekChange,
  semaineSub,
  onSemaineSubChange,
  assignFilter,
  onAssignFilterChange,
  board,
  users,
  loading,
  onAssignee,
  onSchedule,
  onArchive,
  onDelete,
  equipeContent = null,
}) => {
  const [collapsed, setCollapsed] = useState({});
  const weekDateRef = useRef(null);

  const openWeekPicker = () => {
    const input = weekDateRef.current;
    if (!input) return;
    if (typeof input.showPicker === 'function') {
      try {
        input.showPicker();
        return;
      } catch {
        // fallback below
      }
    }
    input.click();
  };

  const onWeekDatePicked = (e) => {
    const value = e.target.value;
    if (!value) return;
    onWeekChange(formatIsoDate(mondayOf(new Date(`${value}T12:00:00`))));
  };

  const sections = useMemo(() => {
    const people = board.people || [];
    const unscheduled = board.unscheduled || [];
    const list = [];

    if (assignFilter !== 'assigned' && unscheduled.length > 0) {
      list.push({
        key: 'unscheduled',
        title: 'Sans semaine',
        hint: 'À planifier',
        isPerson: false,
        tasks: unscheduled,
        plannedMinutes: unscheduled.reduce((s, t) => s + (Number(t.estimatedMinutes) || 0), 0),
        actualMinutes: unscheduled.reduce((s, t) => s + (Number(t.actualMinutes) || 0), 0),
      });
    }

    people.forEach((group) => {
      const isPerson = Boolean(group.user);
      if (assignFilter === 'assigned' && !isPerson) return;
      if (assignFilter === 'unassigned' && isPerson) return;
      list.push({
        key: isPerson ? `u-${group.user.id}` : 'unassigned-week',
        title: group.user?.name || 'Non assignées',
        hint: isPerson ? '' : 'Semaine courante',
        isPerson,
        user: group.user,
        tasks: group.tasks || [],
        plannedMinutes: group.plannedMinutes || 0,
        actualMinutes: group.actualMinutes || 0,
      });
    });

    return list;
  }, [board, assignFilter]);

  const filterCounts = useMemo(() => {
    const people = board.people || [];
    const unscheduled = board.unscheduled || [];
    let assigned = 0;
    let unassigned = unscheduled.length;
    people.forEach((g) => {
      const n = (g.tasks || []).length;
      if (g.user) assigned += n;
      else unassigned += n;
    });
    return {
      all: assigned + unassigned,
      assigned,
      unassigned,
    };
  }, [board]);

  const strip = useMemo(() => {
    const items = [];
    const unassignedGroup = (board.people || []).find((g) => !g.user);
    const unassignedCount = (unassignedGroup?.tasks?.length || 0) + (board.unscheduled || []).length;
    const unassignedPlanned = (unassignedGroup?.plannedMinutes || 0)
      + (board.unscheduled || []).reduce((s, t) => s + (Number(t.estimatedMinutes) || 0), 0);

    items.push({
      key: 'unassigned-week',
      name: 'Non assignées',
      count: unassignedCount,
      planned: unassignedPlanned,
      isPerson: false,
    });

    (board.people || []).filter((g) => g.user).forEach((g) => {
      const planned = Number(g.plannedMinutes) || 0;
      const actual = Number(g.actualMinutes) || 0;
      const pct = planned > 0 ? Math.min(100, Math.round((actual / planned) * 100)) : (actual > 0 ? 100 : 0);
      items.push({
        key: `u-${g.user.id}`,
        name: g.user.name,
        count: (g.tasks || []).length,
        planned,
        actual,
        pct,
        isPerson: true,
        user: g.user,
      });
    });

    return items;
  }, [board]);

  const jump = (key) => {
    setCollapsed((prev) => ({ ...prev, [key]: false }));
    requestAnimationFrame(() => {
      document.getElementById(`sec-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const remindLater = board.remindLater || [];

  return (
    <div className="tp-board">
      <div className="tp-board__toolbar">
        <AppPillSwitch
          variant="segment"
          options={SEMAINE_SUBVIEWS}
          value={semaineSub}
          onChange={onSemaineSubChange}
          ariaLabel="Sous-vues Semaine"
        />
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="tp-board__week-nav">
            <button
              type="button"
              className="tp-board__week-btn"
              aria-label="Semaine précédente"
              onClick={() => onWeekChange(shiftWeek(week, -1))}
            >
              <Icon icon="mdi:chevron-left" width={22} />
            </button>
            <button
              type="button"
              className="tp-board__week-label"
              aria-live="polite"
              aria-label="Choisir une semaine"
              title="Choisir une semaine"
              onClick={openWeekPicker}
            >
              <strong>{formatWeekLabel(week)}</strong>
            </button>
            <input
              ref={weekDateRef}
              type="date"
              className="tp-board__week-date"
              value={week}
              tabIndex={-1}
              aria-hidden="true"
              onChange={onWeekDatePicked}
            />
            <button
              type="button"
              className="tp-board__week-btn"
              aria-label="Semaine suivante"
              onClick={() => onWeekChange(shiftWeek(week, 1))}
            >
              <Icon icon="mdi:chevron-right" width={22} />
            </button>
          </div>
        </div>
      </div>

      {semaineSub === 'equipe' ? (
        equipeContent
      ) : (
        <>
          {!loading && (
            <div className="tp-board__strip">
              {strip.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className="tp-board__strip-item"
                  onClick={() => jump(item.key)}
                >
                  <div className="tp-board__strip-head">
                    {item.isPerson ? (
                      <span className="tp-board__avatar tp-board__avatar--sm" style={{ background: colorForUser(item.user.id) }}>
                        {initials(item.name)}
                      </span>
                    ) : (
                      <Icon icon="mdi:account-off-outline" width={16} style={{ color: 'var(--app-muted)' }} />
                    )}
                    <span>{(item.name || '').split(/\s+/)[0]}</span>
                    <span className="muted">· {item.count}</span>
                  </div>
                  <div className="tp-board__strip-metric">
                    <strong>{formatMinutes(item.planned)}</strong>
                    <span>prévu</span>
                  </div>
                  {item.isPerson && (
                    <div className="tp-board__progress">
                      <div className="tp-board__progress-track">
                        <div
                          className={`tp-board__progress-fill ${progressTone(item.pct)}`}
                          style={{ width: `${item.pct}%` }}
                        />
                      </div>
                      <span className="tp-board__progress-label">{formatMinutes(item.actual)} validé</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

          <div className="tp-board__filters">
            <AppPillSwitch
              variant="segment"
              options={ASSIGN_FILTERS.map((f) => ({
                ...f,
                count: filterCounts[f.value] ?? 0,
              }))}
              value={assignFilter}
              onChange={onAssignFilterChange}
              ariaLabel="Filtre assignation"
            />
          </div>

          {loading ? (
            <AppEmptyState loading />
          ) : (
            <div className="tp-board__list">
              <div className="tp-board__cols" aria-hidden="true">
                <span className="tp-board__cols-task">Tâche</span>
                <span className="tp-board__cols-assign">Assignée à</span>
                <span className="tp-board__cols-plan">Planning</span>
                <span className="tp-board__cols-time">Prévu / validé</span>
                <span className="tp-board__cols-actions" />
              </div>

              {sections.length === 0 && remindLater.length === 0 ? (
                <div className="tp-board__empty">
                  <div className="tp-board__empty-icon">
                    <Icon icon="mdi:calendar-check" width={22} />
                  </div>
                  <div className="fw-semibold">Aucune tâche sur cette semaine</div>
                </div>
              ) : (
                sections.map((sec) => {
                  const expanded = !collapsed[sec.key];
                  const pct = sec.plannedMinutes > 0
                    ? Math.min(100, Math.round((sec.actualMinutes / sec.plannedMinutes) * 100))
                    : 0;
                  return (
                    <section
                      key={sec.key}
                      id={`sec-${sec.key}`}
                      className="tp-board__section"
                      aria-label={sec.title}
                    >
                      <button
                        type="button"
                        className="tp-board__section-head"
                        aria-expanded={expanded}
                        onClick={() => setCollapsed((prev) => ({ ...prev, [sec.key]: !prev[sec.key] }))}
                      >
                        <div className="tp-board__section-lead">
                          <Icon
                            icon="mdi:chevron-down"
                            width={18}
                            className={`tp-board__chevron${expanded ? '' : ' is-collapsed'}`}
                          />
                          {sec.isPerson ? (
                            <span className="tp-board__avatar" style={{ background: colorForUser(sec.user.id) }}>
                              {initials(sec.title)}
                            </span>
                          ) : (
                            <span className="tp-board__avatar tp-board__avatar--ghost">
                              <Icon icon="mdi:account-off-outline" width={14} />
                            </span>
                          )}
                          <div className="tp-board__section-title">
                            <strong>{sec.title}</strong>
                            <span>{sec.tasks.length} tâche{sec.tasks.length > 1 ? 's' : ''}</span>
                            {sec.hint ? <span>{sec.hint}</span> : null}
                          </div>
                        </div>
                        <div className="tp-board__section-meta">
                          <span>Prévu <strong>{formatMinutes(sec.plannedMinutes)}</strong></span>
                          {sec.isPerson && (
                            <>
                              <span>Validé <strong>{formatMinutes(sec.actualMinutes)}</strong></span>
                              <div className="tp-board__progress-track">
                                <div className={`tp-board__progress-fill ${progressTone(pct)}`} style={{ width: `${pct}%` }} />
                              </div>
                            </>
                          )}
                        </div>
                      </button>
                      {expanded
                        && sec.tasks.map((task) => (
                          <TaskRow
                            key={task.id}
                            task={task}
                            week={week}
                            users={users}
                            onAssignee={onAssignee}
                            onSchedule={onSchedule}
                            onArchive={onArchive}
                            onDelete={onDelete}
                          />
                        ))}
                    </section>
                  );
                })
              )}

              <div className="tp-board__later" id="sec-later">
                <div className="tp-board__later-box">
                  <div className="tp-board__later-head">
                    <span className="tp-board__later-icon">
                      <Icon icon="mdi:alarm" width={18} />
                    </span>
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-baseline gap-2">
                        <strong style={{ fontSize: 15 }}>Rappeler plus tard</strong>
                        <span className="text-muted small">{remindLater.length}</span>
                      </div>
                    </div>
                  </div>
                  {remindLater.length === 0 ? (
                    <div className="tp-board__later-empty">
                      Rien en attente. « Plus tard » sur une tâche la place ici sans personne ni semaine.
                    </div>
                  ) : (
                    remindLater.map((task) => (
                      <TaskRow
                        key={task.id}
                        task={task}
                        week={week}
                        users={users}
                        onAssignee={onAssignee}
                        onSchedule={onSchedule}
                        onArchive={onArchive}
                        onDelete={onDelete}
                        remind
                      />
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TempsPlanningBoard;
